import {
  apiPath,
  LOOKUP_IDS_MAX,
  type GroupsHealth,
  GroupsHealthSchema,
  type PlayersReach,
  type PlayersReachRequest,
  PlayersReachSchema,
  PLAYERS_PAGE_MAX,
  type PlayersPage,
  PlayersPageSchema,
  type PlayersQueryInput,
  type CreateScreenRequest,
  type ScreenSummary,
  ScreenSummarySchema,
  type ManifestPointer,
  ManifestPointerSchema,
  type ScreenManifest,
  ScreenManifestSchema,
  type IceServer,
  IceServersSchema,
  type ScreenStatus,
  ScreenStatusSchema,
} from '@codot/contracts';
import { ApiError, getJson, request, sha256Hex } from './http';

export function fetchScreenStatus(screenId: string): Promise<ScreenStatus> {
  return getJson(apiPath('screenStatus', { screenId }), ScreenStatusSchema);
}

/** STUN + our TURN relay with short-lived credentials (public STUN only when no TURN is configured). */
export function fetchIceServers(): Promise<IceServer[]> {
  return getJson(apiPath('liveIceServers'), IceServersSchema);
}

export function fetchManifestPointer(screenId: string): Promise<ManifestPointer> {
  return getJson(apiPath('screenManifestPointer', { screenId }), ManifestPointerSchema);
}

/**
 * Same integrity path the Android player uses: hash the raw bytes, compare with the pointer,
 * THEN parse. The CMS preview therefore shows exactly what a screen will verify and play.
 */
export async function fetchVerifiedManifest(pointer: ManifestPointer): Promise<ScreenManifest> {
  // Use a same-origin path so the Vite proxy works in dev; the absolute URL is for players/CDN.
  const res = await fetch(new URL(pointer.url).pathname);
  if (!res.ok) throw new ApiError(`Manifest download failed (${res.status})`, res.status);

  const bytes = await res.arrayBuffer();
  const actual = await sha256Hex(bytes);
  if (actual !== pointer.sha256) {
    throw new ApiError(`Manifest hash mismatch: expected ${pointer.sha256}, got ${actual}`, res.status);
  }
  return ScreenManifestSchema.parse(JSON.parse(new TextDecoder().decode(bytes)));
}

/** The Players page, a page at a time (search, filters and sort run on the server). */
export function pagePlayers(q: PlayersQueryInput): Promise<PlayersPage> {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '' && v !== 'all') p.set(k, String(v));
  return request('GET', `${apiPath('playersPage')}?${p}`, PlayersPageSchema);
}

/** These players (any count; asked for in chunks of LOOKUP_IDS_MAX). Unknown or unseen ids are left out. */
export async function screensByIds(ids: readonly string[]): Promise<ScreenSummary[]> {
  const unique = [...new Set(ids)];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += LOOKUP_IDS_MAX) chunks.push(unique.slice(i, i + LOOKUP_IDS_MAX));
  const parts = await Promise.all(chunks.map((c) => request('GET', `${apiPath('screens')}?ids=${c.join(',')}`, ScreenSummarySchema.array())));
  return parts.flat();
}

/** Every player matching a filter (a group's, a tag's), read page by page. */
export async function allPlayers(q: Omit<PlayersQueryInput, 'offset' | 'limit'>): Promise<ScreenSummary[]> {
  const out: ScreenSummary[] = [];
  for (let offset: number | null = 0; offset !== null; ) {
    const page = await pagePlayers({ ...q, offset, limit: PLAYERS_PAGE_MAX });
    out.push(...page.items);
    offset = page.nextOffset;
  }
  return out;
}

/** How many players a publish to these targets reaches. */
export function playersReach(body: PlayersReachRequest): Promise<PlayersReach> {
  return request('POST', apiPath('playersReach'), PlayersReachSchema, body);
}

/** Per group: players online / offline / not connected. */
export function groupsHealth(): Promise<GroupsHealth> {
  return request('GET', apiPath('groupsHealth'), GroupsHealthSchema);
}

/** One player (null when it's gone or not yours to see). */
export async function getScreen(id: string): Promise<ScreenSummary | null> {
  return (await screensByIds([id]))[0] ?? null;
}

/** Every player with a location (map pins). */
export function locatedScreens(): Promise<ScreenSummary[]> {
  return request('GET', `${apiPath('screens')}?located=true`, ScreenSummarySchema.array());
}

/** Some player with a location (app previews that need a real place), or null. */
export async function firstLocatedScreen(): Promise<ScreenSummary | null> {
  return (await request('GET', `${apiPath('screens')}?located=true&limit=1`, ScreenSummarySchema.array()))[0] ?? null;
}

/** Every player. Prefer pagePlayers / screensByIds: this is the whole fleet in one response. */
export function listScreens(): Promise<ScreenSummary[]> {
  return request('GET', apiPath('screens'), ScreenSummarySchema.array());
}

/** Consumes a player seat — may fail with 402 QUOTA_SCREENS_EXCEEDED / TRIAL_EXPIRED. */
export function createScreen(body: CreateScreenRequest): Promise<ScreenSummary> {
  return request('POST', apiPath('screens'), ScreenSummarySchema, body);
}
